import React, { useState } from 'react';
import axios from '../utils/axios';
import {
  Box, Button, TextField, Typography, Paper, IconButton, Tooltip, Collapse, Snackbar, Alert, InputAdornment, Stack
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import SettingsIcon from '@mui/icons-material/Settings';
import { QRCodeCanvas } from "qrcode.react";
import config from '../config';

// Hàm gợi ý tên từ link gốc
function suggestLinkName(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    let slug = u.pathname.split('/').filter(Boolean).pop() || '';
    slug = decodeURIComponent(slug.replace(/[-_]+/g, ' '));
    // Đổi chữ cái đầu mỗi từ thành in hoa cho đẹp
    return slug.replace(/\b\w/g, c => c.toUpperCase());
  } catch {
    return '';
  }
}

function ShortenForm({ token, onShorten }) {
  const [url, setUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [shortLink, setShortLink] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [snack, setSnack] = useState({ open: false, text: '', severity: 'success' });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customShortCode, setCustomShortCode] = useState('');
  const [expireAt, setExpireAt] = useState('');
  const [password, setPassword] = useState('');

  const handleShorten = async (e) => {
    e.preventDefault();
    setMessage('');
    setShortLink('');
    setCopied(false);
    try {
      const payload = { original_url: url, link_name: linkName };
      if (showAdvanced) {
        if (customShortCode) payload.custom_short_code = customShortCode;
        if (expireAt) payload.expire_at = expireAt;
        if (password) payload.password = password;
      }

      let res;
      if (token) {
        res = await axios.post('/links', payload);
        setShortLink(`${config.BASE_URL}/${res.data.link.short_code}`);
        if (onShorten) onShorten(res.data.link);
      } else {
        res = await axios.post('/public/shorten', payload);
        setShortLink(`${config.BASE_URL}/${res.data.short_code}`);
        if (onShorten) onShorten(res.data);
      }

      setUrl('');
      setLinkName('');
      setCustomShortCode('');
      setExpireAt('');
      setPassword('');
    } catch (err) {
      setMessage('Lỗi: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shortLink);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = shortLink;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setSnack({ open: true, text: 'Đã copy link vào clipboard!', severity: 'success' });
      setTimeout(() => setCopied(false), 1000);
    } catch (e) {
      setSnack({ open: true, text: 'Copy không thành công!', severity: 'error' });
    }
  };

  return (
    <Box>
      {/* Form nhập URL */}
      <Box
        component="form"
        onSubmit={handleShorten}
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          alignItems: "center",
          maxWidth: 700,
          mx: "auto",
          flexWrap: { xs: "wrap", sm: "nowrap" }
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Dán link của bạn vào đây"
          value={url}
          onChange={e => {
            const val = e.target.value;
            setUrl(val);
            // Nếu user chưa sửa tên link, thì tự động gợi ý tên khi đổi url
            if (!linkName) setLinkName(suggestLinkName(val));
          }}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <InsertLinkIcon color="primary" />
              </InputAdornment>
            ),
            sx: { height: 56, fontSize: 18, borderRadius: 99, bgcolor: "#fff" }
          }}
          sx={{
            fontSize: 18,
            borderRadius: 99,
            boxShadow: "0 2px 12px #6366f144",
            pr: 0,
            ".MuiInputBase-root": { height: 56, fontSize: 18, borderRadius: 99 },
            ".MuiOutlinedInput-input": { py: 1.5 }
          }}
        />

        <Tooltip title={showAdvanced ? "Ẩn tuỳ chọn nâng cao" : "Tuỳ chọn nâng cao"}>
          <IconButton
            onClick={() => setShowAdvanced(!showAdvanced)}
            sx={{ bgcolor: showAdvanced ? "#6366f1" : "#e0e7ef", color: showAdvanced ? "#fff" : "#6366f1", borderRadius: 99, height: 46, width: 46, ml: { xs: 0, sm: 1 } }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>

        <Button
          variant="contained"
          size="large"
          sx={{
            height: 56,
            px: 5,
            fontWeight: 800,
            fontSize: 20,
            borderRadius: 99,
            minWidth: 155,
            boxShadow: "0 2px 12px #6366f155",
            background: "linear-gradient(90deg, #6366f1 10%, #06b6d4 90%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1.1,
            whiteSpace: "nowrap",
            letterSpacing: 1.2
          }}
          type="submit"
        >
          RÚT GỌN
        </Button>
      </Box>

      {/* Field tên hiển thị */}
      <Box sx={{ maxWidth: 700, mx: "auto", mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Tên hiển thị (gợi ý tự động, có thể sửa)"
          value={linkName}
          onChange={e => setLinkName(e.target.value)}
          placeholder="Nhập tên cho link hoặc để trống để lấy gợi ý"
          sx={{ mt: 1 }}
        />
      </Box>

      {/* Tuỳ chọn nâng cao */}
      <Collapse in={showAdvanced}>
        <Paper elevation={2} sx={{
          p: 2, mb: 2, background: "#eef2ff", borderRadius: 4, maxWidth: 700, mx: "auto"
        }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Tùy chọn mã rút gọn"
              value={customShortCode}
              onChange={e => setCustomShortCode(e.target.value.replace(/[^a-zA-Z0-9\-_]/g, ''))}
              inputProps={{ maxLength: 32 }}
              helperText="Tùy chỉnh mã rút gọn (bỏ trống nếu muốn random)"
              fullWidth
            />
            <TextField
              label="Mật khẩu bảo vệ"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              helperText="Ai có link cần nhập mật khẩu này mới truy cập được"
              fullWidth
            />
            <TextField
              label="Ngày hết hạn"
              type="datetime-local"
              value={expireAt}
              onChange={e => setExpireAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Sau thời hạn này link sẽ tự vô hiệu"
              fullWidth
            />
          </Stack>
        </Paper>
      </Collapse>

      {/* Kết quả rút gọn */}
      <Collapse in={!!shortLink}>
        <Paper
          elevation={2}
          sx={{
            mt: 3,
            p: 3,
            border: "1px solid #e0e7ef",
            background: "#f1f5fd",
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            maxWidth: 600,
            mx: "auto"
          }}
        >
          <Typography fontWeight={600} color="primary" fontSize={18}>
            Link của bạn:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <a
              href={shortLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontWeight: 700,
                color: "#6366f1",
                fontSize: 20,
                textDecoration: "underline"
              }}
            >
              {shortLink}
            </a>
            <Tooltip title={copied ? "Đã copy!" : "Copy"}>
              <IconButton onClick={handleCopy} size="small" color={copied ? "success" : "primary"}>
                <ContentCopyIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
            <Tooltip title="QR Code">
              <IconButton size="small" color="secondary">
                <QrCodeIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ mt: 2 }}>
            <QRCodeCanvas value={shortLink} size={100} />
          </Box>
        </Paper>
      </Collapse>

      {message && (
        <Typography sx={{ mt: 2, color: "red" }}>{message}</Typography>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ShortenForm;
